// src/frontend/data/books/BitcoinFundamentals/data/cap_valor_confianza.js

const CAP_VALOR_CONFIANZA = {
  id: 0, // ajusta este id según tu índice real
  slug: "cap0-valor-y-confianza",
  title: "Antes que nada: el valor y la confianza",
  sections: [
    {
      heading: "¿Por qué empezar hablando de valor y confianza?",
      text: [
        "Antes de entrar a definiciones técnicas de blockchain, Bitcoin o criptomonedas, vale la pena detenernos en algo más básico: **¿por qué algo tiene valor?** y **¿por qué confiamos en ese valor?**",
        "Cada vez que aceptas un billete, haces una transferencia o pagas con una app, estás confiando en que ese símbolo —un número en una pantalla o un papel de colores— será aceptado por otras personas en el futuro.",
        "Es decir: **el dinero funciona porque compartimos una historia común**. Esa historia se sostiene en acuerdos sociales, reglas económicas y, sobre todo, en la confianza que tenemos en quienes emiten y gestionan ese dinero."
      ]
    },
    {
      heading: "¿Qué es el valor en el dinero?",
      text: [
        "En términos simples, podemos decir que algo tiene valor cuando otras personas están dispuestas a entregarnos tiempo, trabajo o recursos a cambio de ello.",
        "El dinero —ya sea efectivo, saldo en el banco o criptomonedas— es solo una herramienta para **representar ese valor** y hacerlo más fácil de intercambiar.",
        "A lo largo de la historia se han usado muchas formas de dinero: sal, granos, metales preciosos, billetes, tarjetas, saldos digitales y ahora también criptomonedas. Lo importante no es el material, sino la confianza en que ese objeto o registro será aceptado mañana.",
        "En otras palabras: el valor del dinero no está solo en el papel, el metal o los bits; está en el **acuerdo colectivo** de que esos símbolos sirven para pagar, ahorrar y medir precios."
      ]
    },
    {
      heading: "La confianza en el dinero tradicional",
      text: [
        "En el sistema financiero actual, la confianza se apoya principalmente en **instituciones**:",
        "🔹 Bancos centrales que emiten la moneda.",
        "🔹 Bancos comerciales que guardan y mueven nuestro dinero.",
        "🔹 Gobiernos y marcos legales que regulan el sistema.",
        "Confiamos en que:",
        "🔹 El banco central no destruirá el valor de la moneda de un día para otro.",
        "🔹 El banco respetará nuestros saldos.",
        "🔹 El sistema legal protegerá nuestros derechos en caso de conflicto.",
        "Este modelo ha permitido que millones de personas usen dinero digital todos los días (transferencias, pagos con tarjeta, banca en línea), pero también trae riesgos: inflación elevada, corralitos, bloqueos de cuentas, censura de pagos o exclusión de personas sin acceso al sistema bancario."
      ]
    },
    {
      heading: "De la confianza en instituciones a la confianza en reglas",
      text: [
        "La gran novedad que trae la tecnología blockchain es que propone **otro tipo de confianza**:",
        "en lugar de depender solo de instituciones, se apoya en **reglas matemáticas, protocolos abiertos y redes distribuidas**.",
        "En un sistema como Bitcoin, la confianza ya no se basa únicamente en que una autoridad se comporte bien, sino en que:",
        "🔹 Las reglas del juego están escritas en un protocolo público.",
        "🔹 Cualquiera puede verificar el historial de transacciones.",
        "🔹 La emisión de nuevas unidades sigue normas conocidas de antemano.",
        "Esto no elimina por completo la necesidad de confianza (siempre hay riesgos y límites), pero cambia el centro de gravedad: **menos confianza implícita, más verificación explícita**."
      ]
    },
    {
      heading: "Propiedades que dan valor a una moneda",
      text: [
        "Para que una forma de dinero sea útil, suele necesitar varias propiedades básicas. Entre las más importantes están:"
      ],
      list: [
        "✅ **Aceptación**: que otras personas y comercios la reciban como medio de pago.",
        "✅ **Escasez creíble**: que no se pueda crear de manera arbitraria y sin límites.",
        "✅ **Divisibilidad**: que permita hacer pagos grandes y pequeños sin problema.",
        "✅ **Durabilidad**: que no se destruya o deteriore fácilmente con el uso.",
        "✅ **Portabilidad**: que sea fácil de mover de un lugar a otro.",
        "✅ **Verificabilidad**: que cualquiera pueda comprobar si es auténtica.",
        "✅ **Previsibilidad**: que sus reglas de emisión y funcionamiento sean claras."
      ],
      textAfter: [
        "Las monedas tradicionales y las criptomonedas cumplen estas propiedades de maneras distintas. Entender estas diferencias te ayudará a evaluar el papel que puede jugar Bitcoin en tu vida financiera."
      ]
    },
    {
      heading: "¿En qué confiamos cuando usamos criptomonedas?",
      text: [
        "Cuando utilizas una criptomoneda como Bitcoin, la confianza se distribuye de otra forma:",
        "🔹 Confías en la **criptografía**, que protege claves y transacciones.",
        "🔹 Confías en la **red de nodos**, que valida y replica el historial.",
        "🔹 Confías en el **código del protocolo**, que define las reglas.",
        "A diferencia del dinero tradicional, no hay un banco central que pueda cambiar las reglas de emisión por decreto. Las normas principales están escritas en el protocolo y cualquier cambio importante requiere consenso de la comunidad.",
        "Eso no significa que el sistema sea perfecto o que no tenga riesgos; significa que el **modelo de confianza** es distinto: menos basado en promesas políticas, más basado en reglas técnicas que cualquiera puede revisar."
      ]
    },
    {
      heading: "Bitcoin como experimento de confianza digital",
      text: [
        "Bitcoin puede entenderse como un gran experimento global que responde una pregunta simple pero profunda:",
        "¿es posible tener dinero digital que funcione sin una autoridad central, solo con reglas técnicas y consenso entre participantes?",
        "Desde 2009, la red ha demostrado que es posible:",
        "🔹 Mantener un historial público de transacciones sin borrados arbitrarios.",
        "🔹 Evitar el doble gasto sin banco central.",
        "🔹 Limitar la emisión máxima a 21 millones de unidades.",
        "Con esto no decimos que Bitcoin sea perfecto ni que sustituya todo lo anterior, pero sí que abre una **nueva forma de organizar el valor y la confianza en el entorno digital**."
      ]
    },
    {
      heading: "Ideas clave para llevarte de este capítulo",
      text: [
        "Antes de aprender cómo funciona la minería, las direcciones o las billeteras, es importante quedarte con estas ideas:",
        "🔹 El dinero, en cualquier forma, se basa en una combinación de **valor percibido** y **confianza compartida**.",
        "🔹 El sistema financiero tradicional apoya esa confianza en bancos, gobiernos y leyes.",
        "🔹 Bitcoin y otras criptomonedas proponen trasladar gran parte de esa confianza hacia **reglas públicas, criptografía y redes distribuidas**.",
        "🔹 Entender “en qué estás confiando” es tan importante como entender “cuánto vale” algo.",
        "En los siguientes capítulos veremos cómo Bitcoin intenta materializar estas ideas en un sistema concreto: direcciones, transacciones, bloques, minería y todo lo que hace posible este nuevo tipo de dinero digital."
      ]
    }
  ]
};

export default CAP_VALOR_CONFIANZA;
